import { useReducer } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import { Box } from '@interstate/components/Box';
import type { CheckBoxEventValue } from '@interstate/components/CheckBox';
import { CheckBox } from '@interstate/components/CheckBox';
import { InformationCircleIcon } from '@interstate/components/Icons/InformationCircleIcon';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { Tooltip } from '@interstate/components/Tooltip';
import { Typography } from '@interstate/components/Typography';

import { kebabCase } from '@makemydeal/dr-common-utils';
import { PaymentGridSource, utils as offerReduxUtils } from '@makemydeal/dr-offer-redux';
import type { StateTree } from '@makemydeal/dr-dash-types';
import * as offerReduxActionCreators from '@makemydeal/dr-dash-store/actions/offerRedux';
import * as offerActionCreators from '@makemydeal/dr-dash-store/actions/offerActionCreators';
import * as manualFieldOverridesActionCreators from '@makemydeal/dr-dash-store/actions/manualFieldOverridesActionCreators';
import {
    invalidateProgramAndRefetch,
    mergeLenderFeeOverrides
} from '@makemydeal/dr-dash-store/actions/programOrchestrationActionCreators';
import * as manualFieldOverridesSelectors from '@makemydeal/dr-dash-store/selectors/manualFieldOverridesSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as vehicleSelectors from '@makemydeal/dr-dash-store/selectors/vehicleSelectors';
import { CALC_ENDPOINTS, CDL_USER_INTENT } from '@makemydeal/dr-dash-store/constants';
import { LEASE, PaymentScheduleTypes, AccelerateVehicleCondition, BALLOON } from '@makemydeal/dr-platform-types';
import type { FeeOverride, IDealerFee } from '@makemydeal/dr-platform-types';
import { FeesCategoriesType } from '@makemydeal/dr-shared-types';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { dealLockHooks, useTermNotSupported } from '@makemydeal/dr-dash-components';
import { formatLenderProgramDisplay } from '@makemydeal/dr-shared-ui-utils';

import {
    AUTO_SELECT_PROGRAM,
    NO_PROGRAM_FOR_TERM_MESSAGE,
    PRO_RATA_FEE_TYPE_ID,
    TOOLTIP_CONTENT_FINANCE,
    TOOLTIP_CONTENT_LEASE
} from '../constants';

const { programQuotes, userProgramQuotes } = CALC_ENDPOINTS;

const replaceWithLenderFeeOverrides = (lenderFees: IDealerFee[], feesOverrides: FeeOverride[] | undefined): FeeOverride[] => {
    const isProRataManuallyEdited = feesOverrides?.some((override) => override.feeTypeId === PRO_RATA_FEE_TYPE_ID);
    return lenderFees
        .filter((fee) => {
            if (fee.dealerFeeTypeCode === PRO_RATA_FEE_TYPE_ID) return isProRataManuallyEdited;
            return true;
        })
        .map((fee) => ({
            category: FeesCategoriesType.LENDER,
            feeAmount: fee.dealerFeeAmount,
            feeDescription: fee.dealerFeeDesc,
            feeName: fee.dealerFeeName || fee.dealerFeeDesc,
            feeTypeId: fee.dealerFeeTypeCode,
            includeInCalcIndicator: fee.isFeeIncludedInCalculation ?? fee.includedInCalc ?? true,
            reqCapIndicator: fee.dealerFeeCapped
        }));
};

const AutoSelectProgramScoped = () => {
    const dispatch = useDispatch();
    const store = useStore<StateTree>();
    const [resyncNonce, resyncCheckbox] = useReducer((n: number) => n + 1, 0);

    const currentOfferType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const isLeaseDeal = currentOfferType === LEASE;
    const isBalloon = currentOfferType === BALLOON;

    const paymentGridEnabled: boolean = useSelector((state: StateTree) =>
        offerReduxSelectors.getPaymentGridToggle(state, currentOfferType)
    );
    const isFlexBuyPayment = useSelector(offerReduxSelectors.isFlexBuyPayment) as boolean;
    const isAspPaymentGridRedesignEnabled = useSelector(featureToggleSelectors.isAspPaymentGridRedesignEnabled);
    const isImprovedAutoSelectEnabled = useSelector(featureToggleSelectors.isImprovedAutoSelectEnabled);
    const isProgramIncentivesRestorationEnabled = useSelector(featureToggleSelectors.isProgramIncentivesRestorationEnabled);
    const isLenderFeesPreservedOnLeaseFallback = useSelector(featureToggleSelectors.isLenderFeesPreservedOnLeaseFallbackEnabled);
    // Lease stays togglable with the grid open, as Finance already is; Balloon and FlexBuy keep the lock.
    const disableForPaymentGrid = isAspPaymentGridRedesignEnabled
        ? (isFlexBuyPayment || isBalloon) && paymentGridEnabled
        : (isLeaseDeal || isFlexBuyPayment || isBalloon) && paymentGridEnabled;
    const paymentFrequency = useSelector(offerReduxSelectors.getPaymentFrequency);
    const currentCalcEndpoint = useSelector((state: any) =>
        offerReduxSelectors.getCalculationEndpointByOfferType(state, currentOfferType)
    );
    const isAutoSelectChecked = useSelector((state: StateTree) =>
        manualFieldOverridesSelectors.getIsAutoSelectChecked(state, currentOfferType)
    );

    const theme = useInterstateTheme();
    const isLocked = dealLockHooks.useIsDealLocked();

    // The engine that priced the selected column. It no longer speaks for the checkbox - a column that fell back to
    // user-program-quotes leaves Auto Select Program on, as the deal screen does - but it still reports the fallback
    // the message below explains.
    const selectedCellSource = useSelector((state: StateTree) =>
        offerReduxSelectors.getSelectedPaymentGridCellSource(state, currentOfferType)
    );

    // Gated because the recorded selection outlives the toggle: `featureToggleReducer` re-merges toggle values
    // on INIT_PENCIL_SUCCESS as well as bootstrap, and nothing clears `checkedByOfferType` on that transition.
    // The endpoint fallback covers the redesign-on/F283308-off window, where nothing seeds that selection.
    const isChecked =
        isAspPaymentGridRedesignEnabled || isImprovedAutoSelectEnabled
            ? isAutoSelectChecked ?? currentCalcEndpoint === programQuotes
            : currentCalcEndpoint === programQuotes;
    const vehicleDetails = useSelector(offerReduxSelectors.getVehicleDetails);
    const usedLeasedVehicle = vehicleDetails?.type === AccelerateVehicleCondition.USED && isLeaseDeal;
    const lenderFees = useSelector(offerReduxSelectors.getItemizedLenderFees);
    const feesOverrides = useSelector(offerReduxSelectors.getFeesOverride);
    const hasVehicle = useSelector(vehicleSelectors.getHasVehicle);

    const programName = useSelector(offerReduxSelectors.getSelectedTerm)?.lenderProgram?.programName;
    const lenderLegalName = useSelector(offerReduxSelectors.getLenderLegalName);

    const isTermNotSupported = useTermNotSupported();
    const selectedTermId = useSelector(offerReduxSelectors.getTermIdByOfferType);
    const rejectedTermMonths = offerReduxUtils.parseSelectedTermMonths(selectedTermId);
    const gridColumnHasNoProgram =
        selectedCellSource === PaymentGridSource.UserProgramQuotes || selectedCellSource === PaymentGridSource.Unavailable;
    // Built here rather than in the JSX so the term narrows to a number: a fallback at the call site
    // would be unreachable, since the message only renders when there is a term.
    const noProgramMessage =
        isChecked && rejectedTermMonths && (selectedCellSource ? gridColumnHasNoProgram : isTermNotSupported)
            ? NO_PROGRAM_FOR_TERM_MESSAGE(rejectedTermMonths)
            : undefined;

    const autoSelectLabel =
        isChecked && programName && !noProgramMessage
            ? `${AUTO_SELECT_PROGRAM}: ${formatLenderProgramDisplay(lenderLegalName, programName)}`
            : AUTO_SELECT_PROGRAM;

    const handleAutoSelectProgramChange = (event: InterstateOnChangeEvent<CheckBoxEventValue>) => {
        const { checked } = event.target.value;
        const endPoint = checked ? programQuotes : userProgramQuotes;
        // Turning ASP off is a pricing no-op — never a payment call, and no grid rebuild when the grid is open.
        const skipPaymentAndRebuild = isAspPaymentGridRedesignEnabled && !checked;
        const resetDownpayment =
            isLeaseDeal && paymentFrequency === PaymentScheduleTypes.OnePayLease && endPoint === userProgramQuotes;
        const switchMeta = {
            userIntent: CDL_USER_INTENT.ASP_CHECKBOX_TOGGLED,
            analyticsPayload: { toggleState: checked }
        };

        // AD-6/AC5: re-checking invalidates the active program, so the primitive owns the discard and the
        // switch together. On its declined paths nothing dispatches, so `isChecked` is unchanged and React
        // re-renders nothing - which would leave the browser's own click standing on a box the deal contradicts.
        if (checked && isImprovedAutoSelectEnabled) {
            if (invalidateProgramAndRefetch(store, currentOfferType, { switchMeta, ignoreDealerDefault: true })) {
                // Only recorded once the switch is accepted; a declined switch leaves the deal as it was.
                dispatch(manualFieldOverridesActionCreators.setAutoSelectChecked(currentOfferType, checked));
                dispatch(manualFieldOverridesActionCreators.clearManualFieldOverrides());
            } else {
                resyncCheckbox();
            }
            return;
        }

        if (!checked) {
            if (isImprovedAutoSelectEnabled || isAspPaymentGridRedesignEnabled) {
                dispatch(manualFieldOverridesActionCreators.setAutoSelectChecked(currentOfferType, checked));
            }
            dispatch(manualFieldOverridesActionCreators.clearManualFieldOverrides());
            dispatch(offerReduxActionCreators.setProgramFallbackOverrides());
            if (isLeaseDeal && lenderFees?.length) {
                const nextFeesOverride = isLenderFeesPreservedOnLeaseFallback
                    ? mergeLenderFeeOverrides(store.getState(), lenderFees)
                    : replaceWithLenderFeeOverrides(lenderFees, feesOverrides);
                dispatch(offerActionCreators.updatedFeesOverride(nextFeesOverride, false));
            }
        } else {
            if (isAspPaymentGridRedesignEnabled) {
                dispatch(manualFieldOverridesActionCreators.setAutoSelectChecked(currentOfferType, checked));
            }
            if (isLeaseDeal && feesOverrides?.length) {
                const feesWithoutLender = feesOverrides.filter((fee: any) => fee.category !== FeesCategoriesType.LENDER);
                if (feesOverrides.length !== feesWithoutLender.length) {
                    dispatch(offerActionCreators.updatedFeesOverride(feesWithoutLender, false));
                }
            }
        }

        dispatch(
            offerReduxActionCreators.updateCurrentCalculationEndpoint(endPoint, currentOfferType, {
                withPayment: !skipPaymentAndRebuild,
                ...(skipPaymentAndRebuild && { skipGridRebuild: true }),
                ...(resetDownpayment && { downPayment: null }),
                ...(checked && isProgramIncentivesRestorationEnabled && { includeStandardIncentives: true }),
                ...switchMeta
            })
        );
    };

    return (
        <Box
            data-testid={`${kebabCase(AUTO_SELECT_PROGRAM)}-container`}
            sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '1rem',
                backgroundColor: 'inherit',
                marginBottom: 0
            }}
        >
            <CheckBox
                // Remounted rather than re-rendered on a declined click: the control seeds its own state from
                // `checked`, so an unchanged `isChecked` leaves the browser's click standing and the box reads
                // on while the endpoint and label read off.
                key={`${kebabCase(AUTO_SELECT_PROGRAM)}-${resyncNonce}`}
                className="auto-select-program-checkbox"
                data-testid={`${kebabCase(AUTO_SELECT_PROGRAM)}`}
                id={`${kebabCase(AUTO_SELECT_PROGRAM)}`}
                name={`${kebabCase(AUTO_SELECT_PROGRAM)}`}
                label={autoSelectLabel}
                onChange={handleAutoSelectProgramChange}
                checked={isChecked}
                value={AUTO_SELECT_PROGRAM}
                sx={{
                    backgroundColor: 'inherit'
                }}
                disabled={disableForPaymentGrid || isLocked || usedLeasedVehicle || !hasVehicle}
            />
            <Tooltip size="large" position="bottom" toolTipContent={isLeaseDeal ? TOOLTIP_CONTENT_LEASE : TOOLTIP_CONTENT_FINANCE}>
                <InformationCircleIcon
                    color={theme.tokens.BaseColorBlue500}
                    data-testid={`${kebabCase(AUTO_SELECT_PROGRAM)}-info-icon`}
                />
            </Tooltip>
            {noProgramMessage && (
                // Announced on appearance: this replaces the Term field's Interstate `errorMessage`, which a
                // screen reader read out automatically. Without a live region the swap would silence it. The
                // role lives on a wrapper because Interstate's Typography doesn't accept ARIA props.
                <span role="status" aria-live="polite" data-testid={`${kebabCase(AUTO_SELECT_PROGRAM)}-no-program-message`}>
                    <Typography variant="body-sm" sx={{ color: theme.tokens.BaseColorGray700 }}>
                        {noProgramMessage}
                    </Typography>
                </span>
            )}
        </Box>
    );
};

export default AutoSelectProgramScoped;
